import { AxiosResponse } from 'axios';

/**
 * File-download helpers for the report-export endpoints (Spec #4c).
 *
 * Export endpoints respond with a binary file and a `Content-Disposition`
 * header carrying the server-chosen filename; on error they respond with a JSON
 * body. Because we request `responseType: 'blob'`, even error bodies arrive as a
 * Blob, so the error path needs to read the Blob back into text/JSON.
 */

// Matches both `filename="x"` and RFC 5987 `filename*=UTF-8''x` forms.
const FILENAME_RE = /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i;

const parseFilename = (
  disposition: string | undefined,
  fallback: string
): string => {
  if (!disposition) return fallback;
  const match = FILENAME_RE.exec(disposition);
  if (!match?.[1]) return fallback;
  try {
    return decodeURIComponent(match[1].trim());
  } catch {
    return match[1].trim();
  }
};

/**
 * Turn a blob axios response into a browser file download. The filename is taken
 * from the `Content-Disposition` header when present, otherwise `fallbackName`.
 */
export const triggerBlobDownload = (
  response: AxiosResponse<Blob>,
  fallbackName: string
): void => {
  const filename = parseFilename(
    response.headers?.['content-disposition'],
    fallbackName
  );
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/**
 * Extract a human-readable message from a blob/JSON error response so the UI can
 * surface why an export failed (422 validation, 403 forbidden, 500, etc.).
 */
// eslint-disable-next-line
export const parseBlobErrorMessage = async (error: any): Promise<string> => {
  const fallback = 'Failed to export. Please try again.';
  const data = error?.data;
  if (!data) return error?.statusText || fallback;

  try {
    const text = data instanceof Blob ? await data.text() : data;
    const json = typeof text === 'string' ? JSON.parse(text) : text;
    return json?.message || json?.error || fallback;
  } catch {
    return typeof data === 'string' && data ? data : fallback;
  }
};
